import React, { useState, useRef } from 'react';
import { Upload, Image, Loader2 } from 'lucide-react';
import { apiRequest } from '../config/api';
import { useToast } from '../context/ToastContext';

const LogoUpload = ({ organization, onLogoUpdate, isOwner }) => {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const { showSuccess, showError } = useToast();

  const handleFileSelect = async (file) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      showError('Please select a valid image file (JPG, PNG, SVG, or WebP)');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      showError('File size must be less than 5MB');
      return;
    }

    setUploading(true);

    try {
      const extension = file.type.split('/')[1] === 'svg+xml' ? 'svg' : file.type.split('/')[1];

      const uploadResponse = await apiRequest('/organizations/current/logo/upload-url', {
        method: 'POST',
        body: JSON.stringify({ file_extension: extension }),
      });

      if (!uploadResponse.ok) {
        const error = await uploadResponse.json();
        throw new Error(error.detail || 'Failed to get upload URL');
      }

      const uploadData = await uploadResponse.json();

      const supabaseResponse = await fetch(uploadData.upload_url, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      if (!supabaseResponse.ok) {
        const errorText = await supabaseResponse.text();
        console.error('Supabase upload error:', errorText);
        throw new Error('Failed to upload file to storage');
      }

      const updateResponse = await apiRequest('/organizations/current', {
        method: 'PUT',
        body: JSON.stringify({ logo_url: uploadData.public_url }),
      });

      if (!updateResponse.ok) {
        throw new Error('Failed to update organization logo');
      }

      const updatedOrg = await updateResponse.json();
      onLogoUpdate(updatedOrg);
      showSuccess('Logo uploaded successfully!');

    } catch (error) {
      console.error('Error uploading logo:', error);
      showError(error.message || 'Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveLogo = async () => {
    if (!organization?.logo_url) return;

    try {
      const response = await apiRequest('/organizations/current', {
        method: 'PUT',
        body: JSON.stringify({ logo_url: null }),
      });

      if (!response.ok) {
        throw new Error('Failed to remove logo');
      }

      const updatedOrg = await response.json();
      onLogoUpdate(updatedOrg);
      showSuccess('Logo removed successfully!');
    } catch (error) {
      console.error('Error removing logo:', error);
      showError('Failed to remove logo');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  if (!isOwner) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Image className="h-5 w-5 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-900">Organization Logo</h3>
          </div>
        </div>
        <div className="p-6">
          {organization?.logo_url ? (
            <div className="flex items-center space-x-4">
              <img
                src={organization.logo_url}
                alt={`${organization.name} logo`}
                className="w-16 h-16 object-contain rounded-lg border border-gray-200 bg-gray-50"
              />
              <p className="text-sm text-gray-600">
                Organization logo is set by the owner
              </p>
            </div>
          ) : (
            <p className="text-sm text-gray-600">
              No logo set for this organization
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <Image className="h-5 w-5 text-gray-600" />
          <h3 className="text-lg font-semibold text-gray-900">Organization Logo</h3>
        </div>
      </div>
      <div className="p-6">
        {organization?.logo_url ? (
          <div className="space-y-4">
            <div className="flex items-start space-x-4">
              <img
                src={organization.logo_url}
                alt={`${organization.name} logo`}
                className="w-20 h-20 object-contain rounded-lg border border-gray-200 bg-gray-50"
              />
              <div className="flex-1 space-y-2">
                <p className="text-sm text-gray-600">
                  Current organization logo
                </p>
                <div className="flex space-x-2">
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin inline mr-1" />
                        Uploading...
                      </>
                    ) : (
                      'Replace'
                    )}
                  </button>
                  <button
                    onClick={handleRemoveLogo}
                    disabled={uploading}
                    className="px-3 py-1 text-sm text-red-600 border border-red-300 rounded-md hover:bg-red-50 disabled:opacity-50"
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div
            className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
              dragOver
                ? 'border-brand-400 bg-brand-50'
                : 'border-gray-300 hover:border-gray-400'
            }`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            <div className="space-y-2">
              <Upload className="w-8 h-8 text-gray-400 mx-auto" />
              <div>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="text-brand-600 hover:text-brand-700 font-medium disabled:opacity-50"
                >
                  {uploading ? 'Uploading...' : 'Upload a logo'}
                </button>
                <span className="text-gray-500"> or drag and drop</span>
              </div>
              <p className="text-xs text-gray-500">
                PNG, JPG, SVG or WebP up to 5MB
              </p>
            </div>
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={(e) => handleFileSelect(e.target.files?.[0])}
          className="hidden"
        />
      </div>
    </div>
  );
};

export default LogoUpload;
